import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Plus } from "lucide-react";

import kitchenPhoto from "../../../assets/admin/landing/kitchen.webp";
import { FAQ } from "./data";
import { CONTACT_MAIL, EASE, Em, Magnetic, MaskLines, PillButton, Reveal, SectionTag, WHATSAPP } from "./primitives";

/* ------------------------------------------------------------------ */
/*  FAQ                                                                */
/* ------------------------------------------------------------------ */

function FaqRow({ item, i, open, onToggle }) {
   return (
      <li className="border-b border-lp-ink/10">
         <button onClick={onToggle} aria-expanded={open} className="group flex w-full items-start gap-5 py-6 text-left sm:gap-8 sm:py-7">
            <span className="mt-2 font-ticket text-[11px] text-lp-muted">{String(i + 1).padStart(2, "0")}</span>
            <span
               className={`flex-1 text-[clamp(1.25rem,2.2vw,1.75rem)] font-medium leading-snug tracking-[-0.03em] transition-colors ${
                  open ? "text-lp-ink" : "text-lp-ink/70 group-hover:text-lp-ink"
               }`}
            >
               {item.q}
            </span>
            <motion.span
               animate={{ rotate: open ? 45 : 0 }}
               transition={{ duration: 0.4, ease: EASE }}
               className={`mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border transition-colors ${
                  open ? "border-lp-blue bg-lp-blue text-white" : "border-lp-ink/15 text-lp-ink group-hover:border-lp-ink"
               }`}
            >
               <Plus size={16} />
            </motion.span>
         </button>
         <AnimatePresence initial={false}>
            {open && (
               <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="overflow-hidden"
               >
                  <p className="max-w-2xl pb-8 pl-[calc(11px*2+1.25rem)] pr-12 text-[16px] leading-relaxed text-lp-muted sm:pl-[calc(11px*2+2rem)]">
                     {item.a}
                  </p>
               </motion.div>
            )}
         </AnimatePresence>
      </li>
   );
}

export function Faq() {
   const [open, setOpen] = useState(0);
   return (
      <section id="faq" className="lp-grain relative bg-lp-paper px-5 pb-28 sm:px-8 sm:pb-40">
         <div className="mx-auto grid max-w-[1400px] gap-14 border-t border-lp-ink/10 pt-28 sm:pt-36 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-4">
               <div className="lg:sticky lg:top-28">
                  <SectionTag>FAQ</SectionTag>
                  <MaskLines
                     as="h2"
                     className="mt-8 text-[clamp(2.6rem,5vw,4.6rem)] font-semibold leading-[0.92] tracking-[-0.05em] text-lp-ink"
                     lines={["Vragen aan ", <Em className="text-lp-blue">de chef.</Em>]}
                  />
                  <Reveal className="mt-10 hidden overflow-hidden rounded-3xl lg:block">
                     <div className="relative">
                        <img src={kitchenPhoto} alt="Een kok aan het werk in de keuken" loading="lazy" decoding="async" className="h-64 w-full object-cover" />
                        <a
                           href={WHATSAPP}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="group absolute inset-x-3 bottom-3 flex items-center gap-3 rounded-2xl bg-lp-paper/90 p-3 backdrop-blur transition-colors hover:bg-lp-paper"
                        >
                           <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-lp-ink text-lp-paper">
                              <MessageCircle size={17} />
                           </span>
                           <span className="leading-tight">
                              <span className="block text-sm font-semibold text-lp-ink">Liever even praten?</span>
                              <span className="block text-xs text-lp-muted">Stuur ons een WhatsApp, we antwoorden snel.</span>
                           </span>
                        </a>
                     </div>
                  </Reveal>
               </div>
            </div>

            <ul className="border-t border-lp-ink/10 lg:col-span-8">
               {FAQ.map((item, i) => (
                  <FaqRow key={item.q} item={item} i={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
               ))}
            </ul>
         </div>
      </section>
   );
}

/* ------------------------------------------------------------------ */
/*  Slot-CTA                                                           */
/* ------------------------------------------------------------------ */

function RingButton() {
   const ring = "Vraag een demo · Resto-Online · Vraag een demo · Resto-Online · ";
   return (
      <Magnetic strength={0.4}>
         <a
            href={CONTACT_MAIL}
            className="group relative flex h-44 w-44 items-center justify-center rounded-full bg-lp-blue text-white transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] hover:scale-105 sm:h-52 sm:w-52"
         >
            <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full animate-spin-slow" aria-hidden>
               <defs>
                  <path id="lp-ring" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" />
               </defs>
               <text className="fill-white/80 font-ticket text-[11.5px] uppercase tracking-[0.2em]">
                  <textPath href="#lp-ring">{ring}</textPath>
               </text>
            </svg>
            <span className="relative font-editorial text-3xl italic transition-transform duration-500 group-hover:scale-110">Service!</span>
         </a>
      </Magnetic>
   );
}

export function FinalCta() {
   return (
      <section className="lp-grain lp-grain-dark relative overflow-hidden bg-lp-ink px-5 pb-16 pt-28 text-lp-paper sm:px-8 sm:pt-40">
         <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/3 h-[560px] w-[560px] -translate-x-1/2 rounded-full bg-lp-blue/20 blur-[160px]" />
         <div className="relative mx-auto max-w-[1400px]">
            <SectionTag dark>
               Klaar?
            </SectionTag>
            <div className="mt-10 flex flex-col gap-12 lg:flex-row lg:items-end lg:justify-between">
               <MaskLines
                  as="h2"
                  className="text-[clamp(3.4rem,10vw,10rem)] font-semibold leading-[0.88] tracking-[-0.055em]"
                  lines={["Klaar voor ", <Em className="text-lp-blue">service?</Em>]}
               />
               <div className="flex flex-col items-start gap-8 lg:items-center lg:pb-4">
                  <RingButton />
               </div>
            </div>
            <Reveal className="mt-14 flex flex-col gap-6 border-t border-white/10 pt-10 sm:flex-row sm:items-center sm:justify-between">
               <p className="max-w-md text-lg text-lp-paper/60">
                  We zetten je webshop graag samen met je op. Plan een demo of stuur ons gewoon een berichtje.
               </p>
               <div className="flex flex-wrap gap-3">
                  <PillButton href={CONTACT_MAIL} variant="paper">
                     Mail ons
                  </PillButton>
                  <PillButton href={WHATSAPP} target="_blank" rel="noopener noreferrer" variant="ghost-dark">
                     WhatsApp
                  </PillButton>
               </div>
            </Reveal>
         </div>
      </section>
   );
}
